import React from 'react';
import { Popup, Separator } from 'features/nekst-widgets';
import {
  BaseInputProps,
  Button,
  ButtonSize,
  ErrorsWrapper,
  RadioButtonsGroup,
  SimpleBlock,
  usePopupManager
} from 'ui-builder';
import {
  ContactSyncDirection,
  Person,
  useContactLinkService,
  useContactsSearchService,
  useSyncContactService,
  ValidationError,
} from 'nekst-api';
import { useFubConnectionContextRequired } from '../../../Integrations';
import { useDataLoaderNew } from 'data-loader';
import { SearchResultsRadioButtonsGroup } from './SearchResultsRadioButtonsGroup';

export function ChooseDirectionRadioButtons(
  props: BaseInputProps
) {
  return (
    <RadioButtonsGroup
      options={[
        {
          label: 'Update Nekst Contact',
          value: ContactSyncDirection.EXTERNAL_TO_LOCAL,
        },
        {
          label: 'Update FUB Contact',
          value: ContactSyncDirection.LOCAL_TO_EXTERNAL,
        }
      ]}
      source={props.source}
      onChangeCallback={props.onChangeCallback}
      value={props.value}
      label="Once linked, which contact should be updated?"
      hint="This update is required to sync contact information between Nekst and FUB. Future updates will occur automatically"
    />
  );

}

export function LinkExistingContactPopup(
  props: {
    person: Person,
  }
) {
  const contactsSearchService = useContactsSearchService();

  const fubContext = useFubConnectionContextRequired();

  const connectionProfilesIds = fubContext.profiles.map((item) => item.id);

  const profileId = connectionProfilesIds[0];

  const contactLinkService = useContactLinkService();

  const syncContactService = useSyncContactService();

  const popupManager = usePopupManager();

  const [selectedOption, setSelectedOption] = React.useState<string>('');

  const {
    data: searchResults,
    loading
  } = useDataLoaderNew(async () => {
    const emails = [];

    if (props.person.profile.primaryEmail) {
      emails.push(props.person.profile.primaryEmail);
    }

    if (props.person.profile.alternateEmail) {
      emails.push(props.person.profile.alternateEmail);
    }

    const phoneNumbers = props.person.profile.phoneNumbers.map((item) => item.number);
    const result = await contactsSearchService.searchContacts({
      name: props.person.profile.name.fullName,
      emails,
      phoneNumbers,
    }, [profileId]);

    return result.filter((item) => !item.personId);
  });

  const [errors, setErrors] = React.useState<string[] | undefined>(undefined);

  const [syncDirection, setSyncDirection] = React.useState<ContactSyncDirection>(ContactSyncDirection.EXTERNAL_TO_LOCAL);

  return (
    <Popup
      title="Link Contact"
      content={(
        <SimpleBlock className="form">
          <ErrorsWrapper name="externalContactId" errors={errors}>
            <SearchResultsRadioButtonsGroup
              source="selectedOption"
              label="Select FUB contact to Link"
              onChangeCallback={(data) => {
                setSelectedOption(data.selectedOption);
              }}
              value={selectedOption}
              searchResults={searchResults}
            />
            {selectedOption && (
              <>
                <Separator />
                <ChooseDirectionRadioButtons
                  value={syncDirection}
                  source="syncDirection"
                  onChangeCallback={(data) => {
                    setSyncDirection(data.syncDirection)
                  }}
                />
              </>
            )}
          </ErrorsWrapper>
        </SimpleBlock>

      )}
      buttons={
        !loading ? (
          <Button
            text="Link Contact"
            size={ButtonSize.BIG}
            isDisabled={!fubContext.loaded}
            onClick={async () => {
              try {
                if (selectedOption !== '') {
                  const result = await contactLinkService.linkContact(
                    props.person.id,
                    {
                      externalContactId: selectedOption,
                      connectionProfileId: profileId,
                    },
                  );

                  if (syncDirection === ContactSyncDirection.EXTERNAL_TO_LOCAL) {
                    await contactLinkService.updateNekstContact(
                      result.id,
                      result.externalLinks[0].id
                    );
                  } else if (syncDirection === ContactSyncDirection.LOCAL_TO_EXTERNAL) {
                    await contactLinkService.updateExternalContact(
                      result.id,
                      result.externalLinks[0].id
                    );
                  }
                } else {
                  await syncContactService.createExternalContact(props.person.id, profileId);
                }

                popupManager.closePopup();
              } catch (e) {
                if (e instanceof ValidationError) {
                  setErrors(e.errors.externalContactId);
                }
              }
            }}
          />
        ) : null}
    />
  );
}
